import {
  Primary13SemiBold,
  SequenceMenu,
  Box,
  Flex
} from '@sequencehq/core-components'
import React, { useEffect, useState } from 'react'
import { Modal, Button } from '@sequencehq/core-components'
import { Banner } from '@sequencehq/core-components'
import { InputSelectControlField } from '@sequencehq/core-components'
import { Checkbox } from '@sequencehq/core-components'
import {
  Primary14SemiBold,
  GreyGrey70,
  RedRed50,
  Primary13Regular
} from '@sequencehq/core-components'
import { CustomerSelect } from 'Customer/index'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import type { Currency } from '@sequencehq/api'
import { useNavigate } from 'react-router-dom'
import { invoicePaths } from 'modules/Invoices/invoices.router'
import { useNotifications } from '@sequencehq/core-components'
import { useAggregatedInvoice } from 'modules/Invoices/hooks/useAggregatedInvoice'
import { usePermissions } from 'lib/rbac'

interface DuplicateInvoiceModalProps {
  invoiceId: string
  onSuccess?: () => void
  isOpen: boolean
  onClose: () => void
}

export const DuplicateInvoiceModal: React.FC<DuplicateInvoiceModalProps> = ({
  invoiceId,
  onSuccess,
  isOpen,
  onClose
}) => {
  const handleClose = () => {
    onClose()
    // Reset form state when modal closes
    setIsFormInitialized(false)
    setSelectedCustomer(undefined)
    setSelectedCurrency(undefined)
    setSelectedGroups([])
    setError(null)
    setIsDuplicating(false)
  }
  // Currency options
  const accountSettingsQuery = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )
  const availableCurrencies = accountSettingsQuery.data?.enabledCurrencies ?? []
  const currencyOptions = availableCurrencies.map(currency => ({
    label: currency,
    value: currency
  }))

  // Load aggregated invoice data
  const { invoice: aggregatedInvoice, isLoading: loading } =
    useAggregatedInvoice(invoiceId)
  const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>()
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>()
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isFormInitialized, setIsFormInitialized] = useState(false)
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const billingScheduleId = aggregatedInvoice?.billingScheduleId

  useEffect(() => {
    if (aggregatedInvoice && !isFormInitialized) {
      // Set default customer and currency from original invoice
      if (aggregatedInvoice.customerId) {
        setSelectedCustomer(aggregatedInvoice.customerId)
      }
      if (aggregatedInvoice.currency) {
        setSelectedCurrency(aggregatedInvoice.currency)
      }
      // Set default selected groups
      if (aggregatedInvoice.lineItemGroups) {
        setSelectedGroups(aggregatedInvoice.lineItemGroups.map(g => g.id))
      }
      setIsFormInitialized(true)
    }
  }, [aggregatedInvoice, isFormInitialized])

  const handleDuplicate = async () => {
    const hasLineItemGroups =
      aggregatedInvoice?.lineItemGroups &&
      aggregatedInvoice.lineItemGroups.length > 0
    if (
      !selectedCustomer ||
      !selectedCurrency ||
      (hasLineItemGroups && selectedGroups.length === 0)
    ) {
      setError(
        hasLineItemGroups
          ? 'Please select customer, currency, and at least one line item group.'
          : 'Please select customer and currency.'
      )
      return
    }
    setError(null)
    setIsDuplicating(true)
    try {
      // 1. Create the new invoice
      const originalBillingPeriod = aggregatedInvoice?.billingPeriod
      const invoiceRes = await dashboard20240730Client.postInvoice({
        customerId: selectedCustomer,
        currency: selectedCurrency as Currency,
        paymentOptions: ['BANK_TRANSFER'],
        billingPeriod: originalBillingPeriod
          ? { ...originalBillingPeriod }
          : undefined
      })
      if (invoiceRes.error || !invoiceRes.data) {
        throw new Error('Failed to duplicate invoice: failed to create invoice')
      }
      const newInvoiceId = invoiceRes.data.id

      // 2. Duplicate selected line item groups and their items
      const originalGroups =
        aggregatedInvoice?.lineItemGroups?.filter(g =>
          selectedGroups.includes(g.id)
        ) || []
      // Extract line items from the selected groups
      const originalItems = originalGroups.flatMap(
        group => group.lineItems || []
      )
      await Promise.all(
        originalGroups.map(async group => {
          const groupRes = await dashboardv99990101Client.postLineItemGroup({
            invoiceId: newInvoiceId,
            body: {
              title: group.title,
              description: group.description,
              taxCategoryId: group.taxCategory?.id
            }
          })
          if (groupRes.error || !groupRes.data) {
            throw new Error(
              'Failed to duplicate invoice: failed to create line item group'
            )
          }
          const newGroupId = groupRes.data.id
          const itemsInGroup = originalItems.filter(
            item =>
              item.groupId === group.id &&
              (item.creditGrantIds ?? []).length === 0 &&
              !item.creditGrantId
          )
          await Promise.all(
            itemsInGroup.map(async item => {
              const itemRes = await dashboardv99990101Client.postLineItem({
                invoiceId: newInvoiceId,
                body: {
                  title: item.title,
                  description: item.description,
                  quantity: item.quantity,
                  rate: item.rate,
                  taxRate: item.taxRate,
                  servicePeriod: item.servicePeriod,
                  groupId: newGroupId,
                  priceId: item.priceId,
                  rateDisplay: item.rateDisplay,
                  externalIds: item.externalIds,
                  revenueClassification: item.revenueClassification,
                  revenueRecognitionMethod: item.revenueRecognitionMethod,
                  productId: item.productId
                }
              })
              if (itemRes.error || !itemRes.data) {
                throw new Error(
                  'Failed to duplicate invoice: failed to create line item'
                )
              }
            })
          )
        })
      )
      setIsDuplicating(false)
      if (onSuccess) onSuccess()
      navigate(invoicePaths.creationFlow(newInvoiceId))
    } catch (err: any) {
      setIsDuplicating(false)
      displayNotification(err.message || 'Failed to duplicate invoice', {
        type: 'error'
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      data-testid="invoices.duplicate.modal"
    >
      <Modal.Header showCloseButton onClose={handleClose}>
        Duplicate Invoice
      </Modal.Header>
      <Modal.Content>
        <Flex direction="column" gap={4} align="stretch">
          {billingScheduleId && (
            <Banner
              variant="warning"
              data-testid="invoices.duplicate.modal.attachmentWarning"
            >
              Duplicating creates a separate, one-off invoice. Any existing
              attachments are not included.
            </Banner>
          )}
          <CustomerSelect
            label="Customer"
            value={selectedCustomer ?? ''}
            onChange={setSelectedCustomer}
            allowCreate={false}
            placeholder="Select customer"
            disabled={isDuplicating}
            data-testid="invoices.duplicate.modal.customerSelect"
          />
          <InputSelectControlField
            label="Currency"
            options={currencyOptions}
            value={selectedCurrency}
            onChange={setSelectedCurrency}
            placeholder="Select currency"
            disabled={isDuplicating}
            data-testid="invoices.duplicate.modal.currencySelect"
          />
          {aggregatedInvoice?.lineItemGroups &&
            aggregatedInvoice.lineItemGroups.length > 0 && (
              <Flex direction="column" gap={2} align="stretch">
                <Box
                  {...Primary13SemiBold}
                  color={GreyGrey70}
                  data-testid="invoices.duplicate.modal.groupLabel"
                >
                  Line item groups
                </Box>
                <Flex direction="column" gap={2} align="stretch">
                  {aggregatedInvoice.lineItemGroups.map(group => (
                    <Checkbox
                      key={group.id}
                      label={group.title}
                      isChecked={selectedGroups.includes(group.id)}
                      disabled={isDuplicating}
                      onChange={e => {
                        const isChecked = e.target.checked
                        if (isChecked) {
                          setSelectedGroups(prev => [...prev, group.id])
                        } else {
                          setSelectedGroups(prev =>
                            prev.filter(id => id !== group.id)
                          )
                        }
                      }}
                      data-testid={`invoices.duplicate.modal.group.${group.id}`}
                    />
                  ))}
                </Flex>
              </Flex>
            )}
          {error && (
            <Box color={RedRed50} {...Primary13Regular}>
              {error}
            </Box>
          )}
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isDuplicating}
          data-testid="invoices.duplicate.modal.cancel"
        >
          Cancel
        </Button>
        <Modal.SubmitButton
          text="Duplicate Invoice"
          onClick={handleDuplicate}
          disabled={loading || isDuplicating}
          submitting={isDuplicating}
          data-testid="invoices.duplicate.modal"
        />
      </Modal.Footer>
    </Modal>
  )
}

export const DuplicateInvoice = (props: {
  invoiceId: string
  onSuccess?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { can } = usePermissions()

  if (!can('org.invoice:create')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        data-testid="invoices.duplicate.menuItem"
        uuid="duplicate-invoice"
        label="Duplicate invoice"
        onClick={e => {
          e.stopPropagation()
          setIsOpen(true)
        }}
      />
      {isOpen && (
        <DuplicateInvoiceModal
          invoiceId={props.invoiceId}
          onSuccess={props.onSuccess}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
